import {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useStores } from './use-stores';
import { AuditLogProps } from '../components/AuditLog/AuditLog';
import logger from '../utils/logger';
import { AuditLogAnyAction } from '../../types/api/AuditLog';

export type AuditLogResponseData = {
  items: AuditLogAnyAction[];
  pagination: {
    pages: number;
    total: number;
  }
}

interface AuditLogResponse {
  data: AuditLogResponseData | null;
  loading: boolean;
  error: Error | null;
}

const RETRY_LIMIT = 3;

const useAuditLog = (params: AuditLogProps): AuditLogResponse => {
  const { newAuthStore } = useStores();
  const [data, setData] = useState<AuditLogResponseData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchAuditLog = useCallback(async () => {
    if (retryCount >= RETRY_LIMIT) {
      logger.warn(`Reached retry limit of ${RETRY_LIMIT} for fetching audit log`);
      return;
    }
    setLoading(true);
    setData(null);
    setError(null);
    try {
      const searchParams = convertAuditLogParamsToSearchParams(params);
      const url = `/api/audit?${searchParams.toString()}`;
      const response:any = await newAuthStore.makeAuthorizedRequest(url);

      setData(response);
      setRetryCount(0);
    } catch (err) {
      setError(err as Error);
      setRetryCount(retryCount + 1);
    } finally {
      setLoading(false);
    }
  }, [newAuthStore, params, retryCount]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  return { data, loading, error };
};

const convertAuditLogParamsToSearchParams = (auditLog: AuditLogProps): URLSearchParams => {
  const params = new URLSearchParams();

  if (auditLog.modelId !== undefined) {
    if (Array.isArray(auditLog.modelId)) {
      auditLog.modelId.forEach((id) => params.append('modelId', id.toString()));
    } else {
      params.set('modelId', (auditLog.modelId || '').toString());
    }
  }

  if (auditLog.modelName !== undefined) {
    if (Array.isArray(auditLog.modelName)) {
      auditLog.modelName.forEach((name) => params.append('modelName', name));
    } else {
      params.set('modelName', auditLog.modelName);
    }
  }

  if (auditLog.user !== undefined) {
    params.set('user', auditLog.user);
  }

  if (auditLog.action !== undefined) {
    params.set('action', auditLog.action);
  }

  if (auditLog.from !== undefined) {
    params.set('from', auditLog.from);
  }

  if (auditLog.to !== undefined) {
    params.set('to', auditLog.to);
  }

  if (auditLog.page !== undefined) {
    params.set('page', auditLog.page.toString());
  }

  if (auditLog.pageSize !== undefined) {
    params.set('pageSize', auditLog.pageSize.toString());
  }

  return params;
};

export default useAuditLog;
